import { WingViewModelTypeInspector } from '../decorators/wing-view-model-type.decorator';
import { BaseViewModel } from '../base-view-model';
import { firstValueFrom, isObservable, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { computed, effect, Injector, Signal, signal, WritableSignal } from '@angular/core';
import { DEFAULT_NAMED_WING_RESPONSIVE_CONFIG, DEFAULT_WING_CONFIG } from './wing-default-config.const';
import { ClassConstructor } from '@nts/std/serialization';
import type { ConfigurableWingViewModelInterface, CoreOrchestratorViewModelInterface, CustomWingViewportConfig, MessageContainerInterface, ResolvedWingViewportConfig, WingConfig, WingModalOptions, WingResponsiveConfig, WingToggleContext, WingToggleGuard, WingUiStateChangedEvent, WingUiStateSnapshot, WingViewModelInterface } from '@nts/std/interfaces';
import { ResolvedWingDisplayMode, WingDisplayMode, WingDockMode, WingPanelState, WingToggleReason, WingToggleVisibility, WingViewportKey } from '@nts/std/types';

/**
 * Resolve display mode, converting 'auto' to concrete push/overlay.
 * @internal
 */
export function resolveDisplayMode(
  mode: WingDisplayMode | undefined,
  fallback: ResolvedWingDisplayMode
): ResolvedWingDisplayMode {
  if (!mode || mode === 'auto') {
    return fallback;
  }

  return mode;
}

/**
 * Determine named viewport (mobile/tablet/desktop) from pixel width.
 * @internal
 */
export function resolveNamedViewport(viewportWidth: number): WingViewportKey {
  if (viewportWidth < 576) {
    return 'mobile';
  }

  if (viewportWidth < 992) {
    return 'tablet';
  }

  return 'desktop';
}

/**
 * Check if a viewport width matches a custom viewport rule.
 * @internal
 */
export function matchesCustomViewport(
  viewportWidth: number,
  rule: CustomWingViewportConfig
): boolean {
  const minOk = rule.min === undefined || viewportWidth >= rule.min;
  const maxOk = rule.max === undefined || viewportWidth <= rule.max;
  return minOk && maxOk;
}

/**
 * Validate wing responsive configuration for correctness.
 * Throws descriptive errors if configuration is invalid.
 * @internal
 * @throws Error if configuration has overlapping rules or missing required fields
 */
export function validateWingResponsiveConfig(config?: WingResponsiveConfig): void {
  const customRules = config?.custom ?? [];

  for (const rule of customRules) {
    if (!rule.id?.trim()) {
      throw new Error('Wing responsive custom rule: "id" is required.');
    }

    if (rule.min === undefined && rule.max === undefined) {
      throw new Error(
        `Wing responsive custom rule "${rule.id}": at least one of "min" or "max" is required.`
      );
    }

    if (
      rule.min !== undefined &&
      rule.max !== undefined &&
      rule.min > rule.max
    ) {
      throw new Error(
        `Wing responsive custom rule "${rule.id}": "min" cannot be greater than "max".`
      );
    }
  }

  for (let i = 0; i < customRules.length; i++) {
    for (let j = i + 1; j < customRules.length; j++) {
      const first = customRules[i];
      const second = customRules[j];

      const firstMin = first.min ?? Number.NEGATIVE_INFINITY;
      const firstMax = first.max ?? Number.POSITIVE_INFINITY;
      const secondMin = second.min ?? Number.NEGATIVE_INFINITY;
      const secondMax = second.max ?? Number.POSITIVE_INFINITY;

      const overlaps = firstMin <= secondMax && secondMin <= firstMax;

      if (overlaps) {
        throw new Error(
          `Wing responsive custom rules "${first.id}" and "${second.id}" overlap.`
        );
      }
    }
  }
}

/**
 * Merge two wing configurations together.
 * Later properties override earlier ones; nested objects are merged recursively.
 * @internal
 */
export function mergeWingConfig(
  current: WingConfig,
  partial: Partial<WingConfig>
): WingConfig {
  return {
    ...current,
    ...partial,
    guards: {
      ...current.guards,
      ...partial.guards,
    },
    modalOptions: {
      ...current.modalOptions,
      ...partial.modalOptions,
      customOverlayClasses: partial.modalOptions?.customOverlayClasses
        ?? current.modalOptions?.customOverlayClasses
        ?? [],
    },
    responsive: {
      ...current.responsive,
      ...partial.responsive,
      named: {
        ...current.responsive?.named,
        ...partial.responsive?.named,
      },
      custom: partial.responsive?.custom ?? current.responsive?.custom ?? [],
    },
  };
}

/**
 * Compute the effective viewport configuration for a given pixel width.
 * Evaluates custom rules first (in order), then named breakpoints, then defaults.
 * Also includes any initialCollapsed override from the viewport config.
 * @internal
 */
export function resolveEffectiveWingViewportConfig(
  viewportWidth: number,
  config?: WingConfig
): ResolvedWingViewportConfig {
  const namedViewport = resolveNamedViewport(viewportWidth);
  const defaultViewportConfig = DEFAULT_NAMED_WING_RESPONSIVE_CONFIG[namedViewport];

  const customRules = config?.responsive?.custom ?? [];
  const matchedCustomRule = customRules.find(rule => matchesCustomViewport(viewportWidth, rule));

  if (matchedCustomRule) {
    return {
      viewport: namedViewport,
      width: matchedCustomRule.width ?? defaultViewportConfig.width,
      mode: resolveDisplayMode(matchedCustomRule.mode, defaultViewportConfig.mode),
      source: 'custom',
      customRuleId: matchedCustomRule.id,
      initialCollapsed: matchedCustomRule.initialCollapsed,
    };
  }

  const namedOverride = config?.responsive?.named?.[namedViewport];

  if (namedOverride) {
    return {
      viewport: namedViewport,
      width: namedOverride.width ?? defaultViewportConfig.width,
      mode: resolveDisplayMode(namedOverride.mode, defaultViewportConfig.mode),
      source: 'named',
      initialCollapsed: namedOverride.initialCollapsed,
    };
  }

  return {
    viewport: namedViewport,
    width: defaultViewportConfig.width,
    mode: defaultViewportConfig.mode,
    source: 'default',
  };
}

/**
 * Base implementation of configurable wing view model.
 * Extends this class to create application-specific wing implementations.
 *
 * Features:
 * - Signal-based reactivity (automatic UI updates when state changes)
 * - Responsive viewport handling (automatically adapts to screen size)
 * - Guard-based toggle control (prevent opens/closes based on app logic)
 * - Backward compatibility with legacy wing system (onWingCollapsed)
 * - Type-safe configuration with validation
 *
 * @template TOrchestratorViewModel - Type of parent orchestrator view model
 * @template TWingInfo - Type of application-specific data carried by wing
 *
 * @example
 * ```typescript
 * @WingViewModelType('my-wing')
 * export class MyWingViewModel extends BaseWingViewModel<MyOrchestratorViewModelInterface> {
 *   constructor(orchestrator: MyOrchestratorViewModelInterface, injector: Injector) {
 *     super(orchestrator, injector);
 *   }
 * }
 *
 * // In orchestrator postInit():
 * const wingVm = new MyWingViewModel(this, injector);
 * wingVm.configure({
 *   showToggle: true,
 *   isModal: true,
 *   modalOptions: {
 *     customOverlayClasses: ['my-wing-overlay'],
 *     isOverlayTransparent: false,
 *   },
 *   responsive: {
 *     named: {
 *       mobile: { width: '100%', mode: 'overlay' },
 *       desktop: { width: '435px', mode: 'push' }
 *     }
 *   }
 * });
 * await wingVm.postInit();
 * ```
 */
export abstract class BaseWingViewModel<
  TOrchestratorViewModel extends CoreOrchestratorViewModelInterface,
  TWingInfo = any
> extends BaseViewModel implements WingViewModelInterface<TWingInfo>, ConfigurableWingViewModelInterface<TWingInfo> {

  protected _wingViewModelType: ClassConstructor<any>;
  private hasAppliedInitialState = false;
  private _orchestratorViewModel: TOrchestratorViewModel;

  /** Emits when wingInfo property changes, allowing pass data to wing content component */
  wingInfoChanged = new Subject<TWingInfo>();

  /** Application-specific data carried by this wing instance */
  wingInfo: TWingInfo = null;

  /** Emits true/false when an async action starts/completes */
  onActionInProgress = new Subject<boolean>();

  // ============ INTERNAL SIGNALS - DO NOT ACCESS DIRECTLY ============
  // These are Angular signals for reactive state management.
  // Use public getter methods (effectiveWidth(), isModal(), etc) instead.

  /** @internal Configuration state signal */
  protected readonly _configSignal: WritableSignal<WingConfig> = signal(DEFAULT_WING_CONFIG);

  /** @internal Panel state signal (open/closed) */
  protected readonly _panelStateSignal: WritableSignal<WingPanelState> = signal('closed');

  /** @internal Viewport width signal in pixels */
  protected readonly _viewportWidthSignal: WritableSignal<number> = signal(window.innerWidth);

  // ============ COMPUTED SIGNALS - READ-ONLY DERIVED STATE ============
  // These compute based on other signals and automatically update when dependencies change

  /** Computed effective viewport configuration based on current width and config */
  readonly effectiveResponsiveConfig: Signal<ResolvedWingViewportConfig> = computed(() =>
    resolveEffectiveWingViewportConfig(this._viewportWidthSignal(), this._configSignal())
  );

  /** Computed effective CSS width for current viewport */
  readonly effectiveWidthSignal: Signal<string> = computed(() =>
    this.effectiveResponsiveConfig().width
  );

  /** Computed effective display mode for current viewport */
  readonly effectiveDisplayModeSignal: Signal<ResolvedWingDisplayMode> = computed(() =>
    this.effectiveResponsiveConfig().mode
  );

  /** Computed toggle button visibility from configuration */
  readonly showToggleSignal: Signal<WingToggleVisibility> = computed(() =>
    this._configSignal().showToggle ?? true
  );

  /** Computed vertical docking mode from configuration */
  readonly dockModeSignal: Signal<WingDockMode> = computed(() =>
    this._configSignal().dockMode ?? 'belowToolbar'
  );

  /** Computed modal state from configuration */
  readonly isModalSignal: Signal<boolean> = computed(() =>
    this._configSignal().isModal ?? false
  );

  /** Computed modal backdrop options from configuration */
  readonly modalOptionsSignal: Signal<Required<WingModalOptions>> = computed(() => ({
    customOverlayClasses: this._configSignal().modalOptions?.customOverlayClasses ?? [],
    isOverlayTransparent: this._configSignal().modalOptions?.isOverlayTransparent ?? false,
  }));

  /** Backward-compatible alias for custom overlay classes. Prefer modalOptions() in new code. */
  readonly customOverlayClassesSignal: Signal<string[]> = computed(() =>
    this.modalOptionsSignal().customOverlayClasses
  );

  /** Backward-compatible alias for overlay transparency. Prefer modalOptions() in new code. */
  readonly isOverlayTransparentSignal: Signal<boolean> = computed(() =>
    this.modalOptionsSignal().isOverlayTransparent
  );

  private readonly wingStateChanged$ = new Subject<WingUiStateChangedEvent>();
  readonly stateChanged$: Observable<WingUiStateChangedEvent> = this.wingStateChanged$.asObservable();

  get orchestratorViewModel(): TOrchestratorViewModel {
    return this._orchestratorViewModel;
  }

  /**
   * Canonical wing panel state.
   * Use this for all new code.
   */
  get panelState(): WingPanelState {
    return this._panelStateSignal();
  }

  /**
   * Legacy boolean alias for panel state.
   * true = panel is closed/collapsed
   * false = panel is open/expanded
   * Prefer panelState and requestOpen/requestClose/requestToggle for new code.
   *
   * IMPORTANT: Setter now evaluates guards (does NOT bypass them)
   * Use requestOpen/requestClose/requestToggle for guaranteed guard evaluation
   */
  get isCollapsed(): boolean {
    return this.panelState === 'closed';
  }

  /**
   * Setting isCollapsed directly will attempt to evaluate guards.
   * If guards block the change, this setter will not change the state.
   * This prevents animation glitches when guards prevent state changes.
   *
   * NOTE: This is async-friendly now - it evaluates guards before changing state
   * For immediate state changes without guard evaluation, use the internal signal directly
   */
  set isCollapsed(value: boolean) {
    const targetState: WingPanelState = value ? 'closed' : 'open';
    const currentState = this._panelStateSignal();

    // If already in target state, do nothing
    if (currentState === targetState) {
      return;
    }

    // Request state change through the guard evaluation pipeline
    // Fire and forget - we don't wait for the result
    // Direct property assignment is treated as programmatic intent.
    this.requestStateChange(targetState, 'programmatic').catch(() => {
      // Guard blocked the change - do nothing
    });
  }

  /**
   * Creates an instance of BaseWingViewModel.
   * Validates that @WingViewModelType decorator is applied.
   * Sets up reactive bindings for window resize and legacy interop.
   *
   * @param orchestratorViewModel - Parent orchestrator managing this wing
   * @param injector - Angular injector for effects and lifecycle management
   * @throws Error if @WingViewModelType decorator not applied to subclass
   */
  constructor(
    orchestratorViewModel: TOrchestratorViewModel,
    private readonly injector: Injector,
  ) {
    super();
    this._orchestratorViewModel = orchestratorViewModel;
    this._wingViewModelType = WingViewModelTypeInspector.getValue(this);

    if (this._wingViewModelType === undefined) {
      throw new Error(
        `MetaData ${WingViewModelTypeInspector.META_DATA_KEY} not defined. You must use ${WingViewModelTypeInspector.DECORATOR_NAME}} in ${this.constructor.name}.`
      );
    }

    this.bindLegacyCollapsedInterop();
    this.bindWindowResize();
    this.bindUiStateChangesEmitter();

    this.wingInfoChanged
      .pipe(takeUntil(this.destroySubscribers$))
      .subscribe((res) => {
        this.wingInfo = res;
      });
  }

  /**
   * Called after construction, before view model is used.
   * Override to perform async initialization.
   */
  async postInit(): Promise<void> {
    // Override in subclass for async initialization
  }

  override removeError(item: MessageContainerInterface) {
    throw new Error("Method not implemented.");
  }

  // ============ PUBLIC GETTER METHODS - PREFER THESE OVER SIGNALS ============

  /**
   * Check if toggle button should be visible.
   */
  showToggle(): WingToggleVisibility {
    return this.showToggleSignal();
  }

  /**
   * Get the vertical docking position for the wing panel.
   */
  dockMode(): WingDockMode {
    return this.dockModeSignal();
  }

  /**
   * Check whether the wing should render a modal overlay backdrop.
   */
  isModal(): boolean {
    return this.isModalSignal();
  }

  /**
   * Get modal backdrop options.
   */
  modalOptions(): Required<WingModalOptions> {
    return this.modalOptionsSignal();
  }

  /**
   * Get the effective CSS width for the current viewport.
   */
  effectiveWidth(): string {
    return this.effectiveWidthSignal();
  }

  /**
   * Get the effective display mode for the current viewport.
   */
  effectiveDisplayMode(): ResolvedWingDisplayMode {
    return this.effectiveDisplayModeSignal();
  }

  // ============ CONFIGURATION METHODS ============

  /**
   * Apply complete wing configuration, replacing all previous settings.
   * Configuration is deep-merged with defaults for any undefined properties.
   * On first call, applies the initial collapsed state (respects isInitialCollapsed and viewport overrides).
   *
   * @param config - Complete configuration object
   * @throws Error if configuration is invalid
   *
   * @example
  * wingVm.configure({
  *   isInitialCollapsed: false,  // Start open
  *   showToggle: true,
  *   dockMode: 'belowToolbar',
  *   isModal: true,
    *   modalOptions: {
    *     customOverlayClasses: ['wing-overlay'],
    *     isOverlayTransparent: false,
    *   },
  *   responsive: {
   *     named: {
   *       mobile: { width: '100%', mode: 'overlay', initialCollapsed: true },
   *       desktop: { width: '435px', mode: 'push', initialCollapsed: false }
   *     }
   *   }
   * });
   */
  configure(config: WingConfig): void {
    validateWingResponsiveConfig(config.responsive);
    const mergedConfig = mergeWingConfig(DEFAULT_WING_CONFIG, config);
    this._configSignal.set(mergedConfig);

    // Apply initial collapsed state on first configure() call
    if (!this.hasAppliedInitialState) {
      this.hasAppliedInitialState = true;
      this.applyInitialState(mergedConfig);
    }
  }

  /**
   * Update specific configuration properties without affecting others.
   * Useful for changing single settings after initial configure() call.
   * Does NOT reset the initial state - only configure() applies initial state.
   *
   * @param partial - Partial configuration with only properties to change
   * @throws Error if resulting configuration would be invalid
   *
   * @example
  * wingVm.updateConfig({ isModal: false });
   * wingVm.updateConfig({
   *   responsive: {
   *     named: {
   *       mobile: { width: '90%' }
   *     }
   *   }
   * });
   */
  updateConfig(partial: Partial<WingConfig>): void {
    const merged = mergeWingConfig(this._configSignal(), partial);
    validateWingResponsiveConfig(merged.responsive);
    this._configSignal.set(merged);
    // NOTE: updateConfig does NOT apply initial state - only configure() does
  }

  // ============ PANEL CONTROL METHODS ============

  /**
   * Programmatically request to open the wing panel.
   * Evaluates guards before allowing. Respects if already open.
   *
   * @param reason - Why the open was requested (default: 'programmatic')
   * @returns true if successful, false if prevented by guard or validation
   *
   * @example
   * const allowed = await wingVm.requestOpen('programmatic');
   * if (!allowed) {
   *   console.log('Open was prevented by a guard');
   * }
   */
  async requestOpen(reason: WingToggleReason = 'programmatic'): Promise<boolean> {
    return this.requestStateChange('open', reason);
  }

  /**
   * Programmatically request to close the wing panel.
   * Evaluates guards before allowing. Respects if already closed.
   *
   * @param reason - Why the close was requested (default: 'programmatic')
   * @returns true if successful, false if prevented by guard or validation
   *
   * @example
   * const allowed = await wingVm.requestClose('user');
   */
  async requestClose(reason: WingToggleReason = 'programmatic'): Promise<boolean> {
    return this.requestStateChange('closed', reason);
  }

  /**
   * Programmatically toggle the wing panel between open and closed.
   * Evaluates guards before allowing.
   *
   * @param reason - Why the toggle was requested (default: 'programmatic')
   * @returns true if successful, false if prevented by guard
   *
   * @example
   * await wingVm.requestToggle('user');
   */
  async requestToggle(reason: WingToggleReason = 'programmatic'): Promise<boolean> {
    const nextState: WingPanelState = this._panelStateSignal() === 'open'
      ? 'closed'
      : 'open';

    return this.requestStateChange(nextState, reason);
  }

  // ============ PROTECTED INTERNAL METHODS ============

  /**
   * Update the tracked viewport width (typically called on window resize).
   * @internal
   */
  protected setViewportWidth(viewportWidth: number): void {
    this._viewportWidthSignal.set(viewportWidth);
  }

  /**
   * Resolve and apply the initial collapsed state from configuration.
   * Called on first configure() call only.
   * Respects viewport-specific initialCollapsed override if present.
   * @internal
   */
  private applyInitialState(config: WingConfig): void {
    const effectiveConfig = this.effectiveResponsiveConfig();

    // Check if viewport has initialCollapsed override
    const viewportInitialCollapsed = effectiveConfig.initialCollapsed;
    if (viewportInitialCollapsed !== undefined) {
      this._panelStateSignal.set(viewportInitialCollapsed ? 'closed' : 'open');
      return;
    }

    // Otherwise use global isInitialCollapsed
    const globalInitialCollapsed = config.isInitialCollapsed ?? true;
    this._panelStateSignal.set(globalInitialCollapsed ? 'closed' : 'open');
  }

  /**
   * Internal method to request state change with guard evaluation.
   * Called by requestOpen, requestClose, requestToggle.
   * @internal
   */
  protected async requestStateChange(
    nextState: WingPanelState,
    reason: WingToggleReason
  ): Promise<boolean> {
    const currentState = this._panelStateSignal();

    if (currentState === nextState) {
      return true;
    }

    const context: WingToggleContext = {
      currentState,
      nextState,
      reason,
    };

    const canToggle = await this.evaluateGuard(this._configSignal().guards?.canToggle, context);
    if (!canToggle) {
      return false;
    }

    if (nextState === 'open') {
      const canOpen = await this.evaluateGuard(this._configSignal().guards?.canOpen, context);
      if (!canOpen) {
        return false;
      }
    }

    if (nextState === 'closed') {
      const canClose = await this.evaluateGuard(this._configSignal().guards?.canClose, context);
      if (!canClose) {
        return false;
      }
    }

    this._panelStateSignal.set(nextState);
    return true;
  }

  /**
   * Evaluate a guard function (can be sync, Promise, or Observable).
   * Returns true if no guard provided (guards are permissive by default).
   * @internal
   */
  protected async evaluateGuard(
    guard: WingToggleGuard | undefined,
    context: WingToggleContext
  ): Promise<boolean> {
    if (!guard) {
      return true;
    }

    const result = guard(context);

    if (isObservable(result)) {
      return await firstValueFrom(result);
    }

    if (result instanceof Promise) {
      return await result;
    }

    return result;
  }

  /**
   * Setup window resize listener to track viewport width changes.
   * Called automatically in constructor.
   * @internal
   */
  protected bindWindowResize(): void {
    if (typeof window === 'undefined') {
      return;
    }

    const listener = () => {
      this._viewportWidthSignal.set(window.innerWidth);
    };

    window.addEventListener('resize', listener);

    effect((onCleanup) => {
      onCleanup(() => {
        window.removeEventListener('resize', listener);
      });
    }, { injector: this.injector });
  }

  private bindUiStateChangesEmitter(): void {
    let previous: WingUiStateSnapshot | null = null;

    effect(() => {
      const current: WingUiStateSnapshot = {
        panelState: this._panelStateSignal(),
        showToggle: this.showToggleSignal(),
        dockMode: this.dockModeSignal(),
        isModal: this.isModalSignal(),
        modalOptions: this.modalOptionsSignal(),
        effectiveWidth: this.effectiveWidthSignal(),
        effectiveDisplayMode: this.effectiveDisplayModeSignal(),
      };

      if (!previous) {
        previous = current;
        return;
      }

      const changed = (Object.keys(current) as Array<keyof WingUiStateSnapshot>)
        .filter((key) => previous[key] !== current[key]);

      if (changed.length > 0) {
        const event: WingUiStateChangedEvent = {
          previous,
          current,
          changed,
        };

        // Emit outside effect/computed context to allow subscribers
        // to safely call APIs that write signals (e.g. updateConfig).
        queueMicrotask(() => {
          this.wingStateChanged$.next(event);
        });
      }

      previous = current;
    }, { injector: this.injector });
  }

  /**
   * Setup two-way binding with legacy wing system (onWingCollapsed).
   * Allows old code to work with new wing system.
   * @internal
   */
  private syncingFromLegacy = false;
  private syncingToLegacy = false;

  protected bindLegacyCollapsedInterop(): void {
    let skipInitialLegacyEmission = true;

    // Listen for legacy onWingCollapsed changes and sync to new system
    this.orchestratorViewModel.eventDispatcher.onWingCollapsed.subscribe(

      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      async (collapsed) => {
        // Ignore only the first BehaviorSubject seed emission to avoid startup flicker.
        // All subsequent legacy emissions are processed even when configure() is never called.
        if (skipInitialLegacyEmission) {
          skipInitialLegacyEmission = false;
          return;
        }

        if (this.syncingToLegacy) {
          return;
        }

        this.syncingFromLegacy = true;
        try {
          if (collapsed) {
            await this.requestClose('legacy');
          } else {
            await this.requestOpen('legacy');
          }
        } finally {
          this.syncingFromLegacy = false;
        }
      }
    );

    // Listen for new system changes and sync to legacy onWingCollapsed
    effect(() => {
      const collapsed = this._panelStateSignal() === 'closed';

      if (this.syncingFromLegacy) {
        return;
      }

      if (this.orchestratorViewModel.eventDispatcher.onWingCollapsed.value === collapsed) {
        return;
      }

      this.syncingToLegacy = true;
      try {
        this.orchestratorViewModel.eventDispatcher.onWingCollapsed.next(collapsed);
      } finally {
        this.syncingToLegacy = false;
      }
    }, { injector: this.injector });
  }
}
